import React from "react";
import { graphql, PageProps, useStaticQuery } from "gatsby";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { SEO } from "@/components/seo";
import { BlogPost } from "@/data/blogs";
import { cn } from "@/lib/utils";
import DynamicImage from "@/components/shared-components/dynamic-image";

interface BlogPostTemplateProps extends PageProps {
  pageContext: {
    blog: BlogPost;
    path?: string;
  };
}

const BlogPostTemplate: React.FC<BlogPostTemplateProps> = ({ pageContext }) => {
  const { blog } = pageContext;

  return (
    <>
      <Navbar type="solid" />
      <main className="bg-surface">
        <article className="px-4 wrap-contain pt-24 pb-24 md:pt-36">
          <h1 className="text-3xl md:text-4xl font-bold mx-auto max-w-2xl text-center mb-8">
            {blog.title}
          </h1>
          {/*<div className="flex items-center justify-center gap-3 mb-8">*/}
          {/*  <img */}
          {/*    src={blog.authorImage}*/}
          {/*    alt={blog.author}*/}
          {/*    className="w-10 h-10 rounded-full"*/}
          {/*  />*/}
          {/*  <div>*/}
          {/*    <p className="font-medium">{blog.author}</p>*/}
          {/*    <p className="text-gray-600 text-xs">{blog.date}</p>*/}
          {/*  </div>*/}
          {/*</div>*/}
          <DynamicImage
            imageName={blog.coverImage}
            alt={blog.title}
            className="mx-auto max-w-3xl mb-8"
            imageClassName="rounded-lg"
          />
          <div className="flex flex-col gap-6 md:gap-10 max-w-2xl mx-auto">
            {!blog.isTempDescription ? (
              <p className="mb-6">{blog.description}</p>
            ) : null}

            {blog.sections?.map((section, i) => (
              <div key={i}>
                {section.title && (
                  <h2 className="text-2xl font-bold mb-6">{section.title}</h2>
                )}
                {section.description && (
                  <p className={cn(section.pointers?.length ? "mb-3" : "mb-6")}>
                    {section.description}
                  </p>
                )}

                {!!section.pointers?.length && (
                  <ul style={{ listStyleType: "disc", paddingLeft: "2rem" }}>
                    {section.pointers.map((pointer, i) => (
                      <li key={i} className="mb-3">
                        <strong>{pointer.title}</strong> {pointer.description}
                      </li>
                    ))}
                  </ul>
                )}

                {section.subSections?.map((subSection, i) => (
                  <div key={i}>
                    <div className="flex gap-1">
                      {subSection.title && (
                        <h4 className="text-lg mb-2">
                          <strong>{subSection.title}</strong>{" "}
                          {subSection.subTitle ? (
                            <span className="text-base">
                              {subSection.subTitle}
                            </span>
                          ) : (
                            ""
                          )}
                        </h4>
                      )}
                    </div>
                    {subSection.description && (
                      <p className="mb-3">{subSection.description}</p>
                    )}

                    {!!subSection.pointers?.length && (
                      <ul
                        style={{ listStyleType: "disc", paddingLeft: "2rem" }}
                      >
                        {subSection.pointers.map((pointer, i) => (
                          <li key={i} className="mb-3">
                            <strong>{pointer.title}</strong>{" "}
                            {pointer.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default BlogPostTemplate;

export const Head: React.FC<BlogPostTemplateProps> = ({ pageContext }) => {
  const { blog, path } = pageContext;

  const data = useStaticQuery(graphql`
    query {
      allFile(filter: { sourceInstanceName: { eq: "images" } }) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(formats: [AUTO, WEBP])
          }
        }
      }
    }
  `);

  // Find the matching image based on the name
  const image = data.allFile.nodes.find(
    (node: { relativePath: string }) => node.relativePath === blog.coverImage
  );

  return (
    <SEO
      title={`DevsLane - Blog - ${blog.title}`}
      description={blog.description}
      pathname={path}
      seoImage={image.childImageSharp.gatsbyImageData.images.fallback.src}
    />
  );
};
